import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Identity verification gate: opens when an unverified user tries a gated
// action (swipe, message, pay, publish). Browsing stays free for everyone;
// the real actions unlock after the one time Didit ID check, which lives on
// the profile page. This modal never fakes a verified state — it only sends
// the user to the real verification card.
export default function VerifyGateModal({ open, onClose }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Verify your identity"
            className="relative w-full rounded-t-3xl bg-night-surface p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-ambient sm:max-w-md sm:rounded-3xl sm:pb-6"
          >
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="absolute right-3 top-3 rounded-full p-2 text-night-sub transition hover:bg-night hover:text-night-text"
            >
              <X size={18} />
            </button>

            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-night-tint text-brand-primary">
              <ShieldCheck size={22} strokeWidth={2} />
            </span>
            <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight text-night-text">
              Verify your identity
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-night-sub">
              SwipRent is a verified community. Browsing is free for everyone, but swiping,
              matching, messaging and payments unlock after a quick one time ID check. It takes
              about two minutes.
            </p>

            <button
              type="button"
              onClick={() => {
                onClose();
                navigate("/profile");
              }}
              className="mt-6 w-full rounded-full bg-brand-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-primary-hover"
            >
              Get verified
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 w-full rounded-full px-4 py-3 text-sm font-semibold text-night-sub transition hover:bg-night hover:text-night-text"
            >
              Keep browsing
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
